<template>
  <b-container fluid class="p-0 fullContent">
    <b-row v-if="loading">
      <b-col class="px-md-3 px-lg-6 py-5 shadow bg-white" offset-xl="2" xl="8">
        <LoadingSpinner message="Loading Original Landowner Report..." />
      </b-col>
    </b-row>
    <b-row v-else-if="error">
      <b-col class="px-md-3 px-lg-6 py-5 shadow bg-white" offset-xl="2" xl="8">
        <b-alert variant="danger" show>
          {{ error }}
        </b-alert>
      </b-col>
    </b-row>
    <b-container v-else ref="container" class="position-relative fullContent" fluid>
      <b-row class="pb-lg-0 pb-md-8 pb-0">
        <b-col class="px-3 pb-5 px-6-md pb-2-md shadow bg-white" offset-xl="2" xl="8">
          <div class="d-flex justify-content-between py-3">
            <div>
              <h2>Original Landowner Report Summary</h2>
              <router-link tag="a" :to="`/notification/${notification.id}`">
                Notification #{{notification.fonNumber}}
              </router-link>
              <p class="text-muted mt-1 mb-0">
                This is the original report before it was edited.
                Archived on {{ archivedReport.archivedAt | dateString }}.
              </p>
            </div>
            <div>
              <b-button
                class="hide-on-print w-100 w-md-auto my-3"
                @click="printReport" pill>
                Print this Report
              </b-button>
            </div>
          </div>
          <SummaryCard
            id="generalDetailsCard"
            title="General Details"
            :collapsible="false"
            :value="false"
          >
            <GeneralDetails />
          </SummaryCard>
          <SummaryCard
            id="involvementDetailsCard"
            title="Involvement Details"
            :collapsible="false"
            :value="false"
          >
            <InvolvementDetails />
          </SummaryCard>
          <SummaryCard
            id="activityDetailsCard"
            title="Activity Details"
            :collapsible="false"
            :value="false"
          >
            <ActivityDetails />
          </SummaryCard>
          <SummaryCard
            id="stumpageDetailsCard"
            title="Stumpage Details"
            :collapsible="false"
            :value="false"
          >
            <StumpageDetails />
          </SummaryCard>
        </b-col>
      </b-row>
    </b-container>
  </b-container>
</template>

<script>
import { mapState } from 'vuex';
import Vue from 'vue';
import ActivityDetails from '@/components/landownerreport/summary/ActivityDetails.vue';
import GeneralDetails from '@/components/landownerreport/summary/GeneralDetails.vue';
import InvolvementDetails from '@/components/landownerreport/summary/InvolvementDetails.vue';
import StumpageDetails from '@/components/landownerreport/summary/StumpageDetails.vue';
import SummaryCard from '@/components/common/SummaryCard.vue';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';

export default {
  name: 'OriginalLandownerReport',
  components: {
    ActivityDetails,
    GeneralDetails,
    InvolvementDetails,
    StumpageDetails,
    SummaryCard,
    LoadingSpinner,
  },
  data() {
    return {
      loading: true,
      error: null,
      archivedReport: null,
      originalReport: null,
    };
  },
  async created() {
    try {
      // Save the current report so we can restore it later
      this.originalReport = { ...this.$store.state.LandownerReport.landownerReport };

      // Fetch the current report to get notificationId, then load notification
      await this.$store.dispatch('LandownerReport/getLandownerReport', this.$route.params.id);
      const notificationId = this.$store.state.LandownerReport.landownerReport.notificationId;
      await this.$store.dispatch('Notification/getNotification', notificationId);
      await this.$store.dispatch('ActivityDetails/fetch');

      // Fetch the archived original
      const archived = await this.$store.dispatch('LandownerReport/getArchivedOriginal', this.$route.params.id);
      this.archivedReport = archived;

      // Overlay the archived data onto the store's landownerReport so summary components render it
      const currentReport = { ...this.$store.state.LandownerReport.landownerReport };
      const overlaid = {
        ...currentReport,
        harvest: archived.harvest,
        nonHarvest: archived.nonHarvest,
        harvestComplete: archived.harvestComplete,
        saleTypeId: archived.saleTypeId,
        generalDetailsFormComplete: archived.generalDetailsFormComplete,
        involvementFormComplete: archived.involvementFormComplete,
        operatorChanged: archived.operatorChanged,
        operatorName: archived.operatorName,
        operatorAddressId: archived.operatorAddressId,
        managementPlan: archived.managementPlan,
        licensedForesterChanged: archived.licensedForesterChanged,
        anotherLicensedForester: archived.anotherLicensedForester,
        licensedForesterName: archived.licensedForesterName,
        licenseNumber: archived.licenseNumber,
        licensedForesterAdded: archived.licensedForesterAdded,
        exported: archived.exported,
        activitiesFormComplete: archived.activitiesFormComplete,
        partialCutHarvest: archived.partialCutHarvest,
        initialShelterwoodHarvest: archived.initialShelterwoodHarvest,
        finalShelterwoodHarvest: archived.finalShelterwoodHarvest,
        landUseChangeCategoryId: archived.landUseChangeCategoryId,
        landUseChangeArea: archived.landUseChangeArea,
        sitePreperationAcres: archived.sitePreperationAcres,
        releaseAcres: archived.releaseAcres,
        nonCommercialThinningAcres: archived.nonCommercialThinningAcres,
        stumpageFormComplete: archived.stumpageFormComplete,
        stumpagePriceTypeId: archived.stumpagePriceTypeId,
        additionalInformation: archived.additionalInformation,
        reportingYear: archived.reportingYear,
        // Clear editor comments so they don't show in the original
        editorCommentGeneralDetails: null,
        editorCommentInvolvement: null,
        editorCommentActivities: null,
        editorCommentStumpage: null,
        editedByUserName: null,
        editedAt: null,
      };
      this.$store.commit('LandownerReport/SET_LANDOWNER_REPORT', overlaid);

      this.loading = false;
    } catch (e) {
      this.error = 'No archived original report found for this landowner report.';
      this.loading = false;
    }
  },
  beforeDestroy() {
    // Restore the original report data when navigating away
    if (this.originalReport && this.originalReport.id) {
      this.$store.dispatch('LandownerReport/getLandownerReport', this.$route.params.id);
    }
  },
  computed: {
    ...mapState('Notification', {
      notification: state => state.notification,
    }),
  },
  methods: {
    printReport() {
      Vue.nextTick(() => window.print());
    },
  },
};
</script>
